import { type Query, type QueryClient, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { api, qs } from "../api/client";
import type { ChatWindow, NowPlaying, User } from "../api/types";
import { usePublicSettings } from "../api/queries/settings";
import { useChatStreamStore } from "../chat/stream";
import { usePlayer } from "../state/player";
import { nativeRuntime, type NativeSocket } from "../platform/native";
import {
  type Change,
  type ClientFrame,
  REALTIME_CLIENT_ID,
  REALTIME_DOMAINS,
  REALTIME_VERSION,
  type RealtimeDomain,
  PLAYBACK_CHANGE_EVENT,
  type ServerFrame,
  parseServerFrame,
} from "./protocol";
import { RefreshScheduler } from "./scheduler";

const FAST_MS = 8_000;
const ORDINARY_MS = 15_000;
const PRESENCE_MS = 20_000;
const VK_LOGIN_POLL_MS = 2_000;
const RECONNECT_MIN_MS = 5_000;
const RECONNECT_MAX_MS = 60_000;
const MAX_SERVER_FRAME = 256 * 1024;

const PREFIXES_BY_DOMAIN: Record<RealtimeDomain, readonly string[]> = {
  segments: [
    "segments",
    "segments-at",
    "segment",
    "matches",
    "split-proposal",
    "timeline",
    "nowplaying",
  ],
  listened: ["segments", "segments-at", "segment", "timeline"],
  preferences: ["me"],
  publications: ["publish", "segments", "segment"],
  "publishing-login": ["publish"],
  settings: ["settings"],
  access: ["development-gate", "me"],
  users: ["users"],
  sessions: ["me", "sessions"],
  audit: ["audit"],
  artwork: ["artwork", "segments", "segment"],
  events: ["events", "boundaries", "timeline", "segments", "segment", "nowplaying"],
  maintenance: ["maintenance"],
  coverage: ["bounds", "waveform", "timeline", "segments", "segment"],
  playback: [],
  chat: ["chat"],
  nowplaying: ["nowplaying"],
};

const FAST_PREFIXES: Record<string, true> = {
  chat: true,
  nowplaying: true,
  maintenance: true,
};

function rootOf(query: Query): string | null {
  return typeof query.queryKey[0] === "string" ? query.queryKey[0] : null;
}

function queryRange(query: Query): { start: number; end: number } | null {
  const root = rootOf(query);
  if (!root || !["timeline", "waveform", "boundaries"].includes(root)) return null;
  const start = typeof query.queryKey[1] === "string" ? Date.parse(query.queryKey[1]) : NaN;
  const end = typeof query.queryKey[2] === "string" ? Date.parse(query.queryKey[2]) : NaN;
  return Number.isFinite(start) && Number.isFinite(end) && start < end ? { start, end } : null;
}

function changeMatchesQuery(change: Change, query: Query): boolean {
  const root = rootOf(query);
  if (!root || !PREFIXES_BY_DOMAIN[change.domain].includes(root)) return false;
  if (change.reset) return true;
  if (change.ids?.length && ["segment", "matches", "split-proposal"].includes(root)) {
    const id = query.queryKey[1];
    if ((typeof id === "number" || typeof id === "string") && !change.ids.includes(id))
      return false;
  }
  const wanted = queryRange(query);
  if (change.range && wanted) {
    const start = Date.parse(change.range.start);
    const end = Date.parse(change.range.end);
    if (
      Number.isFinite(start) &&
      Number.isFinite(end) &&
      (end <= wanted.start || start >= wanted.end)
    ) {
      return false;
    }
  }
  return true;
}

function subscription(client: QueryClient, chatEnabled: boolean): ClientFrame {
  const active = client
    .getQueryCache()
    .findAll()
    .filter((query) => query.isActive());
  const roots = active.map(rootOf).filter((root): root is string => root !== null);
  const domains = REALTIME_DOMAINS.filter((domain) =>
    domain === "chat"
      ? chatEnabled
      : domain === "nowplaying" ||
        domain === "playback" ||
        PREFIXES_BY_DOMAIN[domain].some((prefix) => roots.includes(prefix)),
  );
  const segmentIds = active
    .filter((query) => ["segment", "matches", "split-proposal"].includes(rootOf(query) ?? ""))
    .map((query) => query.queryKey[1])
    .filter((id): id is number => Number.isSafeInteger(id) && (id as number) > 0)
    .slice(0, 64);
  const ranges = active
    .map(queryRange)
    .filter((range): range is { start: number; end: number } => range !== null);
  const timeline = ranges.length
    ? {
        start: new Date(Math.min(...ranges.map((range) => range.start))).toISOString(),
        end: new Date(Math.max(...ranges.map((range) => range.end))).toISOString(),
      }
    : null;
  return {
    type: "subscribe",
    version: REALTIME_VERSION,
    domains,
    segment_ids: [...new Set(segmentIds)],
    timeline,
  };
}

function accountIdentity(client: QueryClient): string {
  const me = client.getQueryData<User | null>(["me"]);
  return JSON.stringify(me ? [me.id, me.role, me.last_login] : null);
}

function accountPending(client: QueryClient): boolean {
  const state = client.getQueryState(["me"]);
  return state !== undefined && state.data === undefined && state.status === "pending";
}

export function startRealtime(client: QueryClient, chatEnabled = false): () => void {
  const scheduler = new RefreshScheduler(client);
  const catchup = new AbortController();
  const runtime = nativeRuntime();
  type RealtimeSocket = WebSocket | NativeSocket;
  let socket: RealtimeSocket | null = null;
  let socketOpen = false;
  let reconnectTimer: number | null = null;
  let fastTimer: number | null = null;
  let ordinaryTimer: number | null = null;
  let vkLoginTimer: number | null = null;
  let presenceTimer: number | null = null;
  let failures = 0;
  let closed = false;
  let epoch: string | null = null;
  let sequence = 0;
  let sentSubscription = "";
  let chatSync: Promise<void> | null = null;
  let syncing = false;
  let resyncPending = false;
  let identity: string | undefined = accountPending(client) ? undefined : accountIdentity(client);
  const available = () => document.visibilityState !== "hidden" && navigator.onLine !== false;
  const isOpen = (current: RealtimeSocket | null): current is RealtimeSocket =>
    current !== null && socket === current && socketOpen;
  let chatCursor: number | null = null;
  const catchUpChat = async (): Promise<void> => {
    let bootstrap = chatCursor === null;
    while (!catchup.signal.aborted) {
      const previous = chatCursor ?? 0;
      const page = await api.get<ChatWindow>(
        `/api/chat${qs(bootstrap ? { latest: true, limit: 200 } : { after: previous, limit: 200 })}`,
        catchup.signal,
      );
      if (catchup.signal.aborted) return;
      useChatStreamStore.getState().pushMany(page.messages);
      const next = page.messages.reduce(
        (highest: number, message) => Math.max(highest, message.id),
        previous,
      );
      chatCursor = next;
      if (bootstrap) {
        bootstrap = false;
        continue;
      }
      if (!page.truncated || next <= previous) return;
    }
  };
  const syncChat = (): Promise<void> => {
    if (!chatEnabled) return Promise.resolve();
    chatSync ??= catchUpChat().finally(() => {
      chatSync = null;
    });
    return chatSync;
  };
  const markFast = () => {
    if (!available()) return;
    void syncChat().catch(() => undefined);
    const current = socket;
    if (isOpen(current) && useChatStreamStore.getState().status !== "streaming") {
      resync(current);
    } else {
      scheduler.mark((query) => FAST_PREFIXES[rootOf(query) ?? ""] === true);
    }
  };
  const markOrdinary = () => {
    if (available()) scheduler.mark((query) => FAST_PREFIXES[rootOf(query) ?? ""] !== true);
  };
  const markVkLogin = () => {
    if (available()) {
      scheduler.mark((query) => rootOf(query) === "publish" && query.queryKey[1] === "vk-qr");
    }
  };
  const startFallback = () => {
    useChatStreamStore.getState().setStatus("polling");
    if (fastTimer !== null) return;
    fastTimer = window.setInterval(markFast, FAST_MS);
    ordinaryTimer = window.setInterval(markOrdinary, ORDINARY_MS);
  };
  const stopFallback = () => {
    if (fastTimer !== null) window.clearInterval(fastTimer);
    if (ordinaryTimer !== null) window.clearInterval(ordinaryTimer);
    fastTimer = null;
    ordinaryTimer = null;
  };
  const send = (frame: ClientFrame) => {
    const current = socket;
    if (isOpen(current)) current.send(JSON.stringify(frame));
  };
  const sendSubscription = () => {
    const frame = subscription(client, chatEnabled);
    const encoded = JSON.stringify(frame);
    if (encoded === sentSubscription) return;
    sentSubscription = encoded;
    const current = socket;
    if (isOpen(current)) current.send(encoded);
  };
  const sendPresence = () => {
    if (runtime !== null) return;
    send({
      type: "presence",
      client_id: REALTIME_CLIENT_ID,
      playing: usePlayer.getState().playing,
    });
  };
  const resync = (current: RealtimeSocket) => {
    if (syncing) {
      resyncPending = true;
      startFallback();
      return;
    }
    syncing = true;
    void Promise.allSettled([scheduler.resync(), syncChat()])
      .then(([snapshots, chat]) => {
        const succeeded =
          snapshots.status === "fulfilled" && snapshots.value && chat.status === "fulfilled";
        if (succeeded && !resyncPending && !closed && socket === current && isOpen(current)) {
          stopFallback();
          useChatStreamStore.getState().setStatus("streaming");
        } else if (!closed && socket === current) {
          startFallback();
        }
      })
      .catch(() => {
        if (!closed && socket === current) startFallback();
      })
      .finally(() => {
        syncing = false;
        if (!resyncPending) return;
        resyncPending = false;
        const latest = socket;
        if (isOpen(latest)) resync(latest);
      });
  };
  const accept = (frame: ServerFrame, current: RealtimeSocket) => {
    if (frame.type === "ready") {
      const changed = epoch !== frame.epoch || sequence !== frame.sequence;
      epoch = frame.epoch;
      sequence = frame.sequence;
      if (changed || useChatStreamStore.getState().status !== "streaming") resync(current);
      return;
    }
    if (frame.epoch !== epoch || frame.sequence !== sequence + 1) {
      epoch = frame.epoch;
      sequence = frame.sequence;
      resync(current);
    } else {
      sequence = frame.sequence;
    }
    if (frame.type === "chat") {
      if (chatEnabled) useChatStreamStore.getState().push(frame.message);
    } else if (frame.type === "nowplaying") {
      client.setQueryData<NowPlaying>(["nowplaying"], (previous) =>
        previous ? { ...previous, ...frame.patch } : previous,
      );
    } else {
      if (frame.changes.some((change) => change.domain === "playback")) {
        window.dispatchEvent(new Event(PLAYBACK_CHANGE_EVENT));
      }
      scheduler.mark((query) => frame.changes.some((change) => changeMatchesQuery(change, query)));
    }
  };
  const scheduleReconnect = () => {
    if (closed || reconnectTimer !== null || !available()) return;
    const ceiling = Math.min(
      RECONNECT_MIN_MS * 2 ** Math.min(Math.max(0, failures - 1), 4),
      RECONNECT_MAX_MS,
    );
    const delay = ceiling * (0.5 + Math.random());
    reconnectTimer = window.setTimeout(() => {
      reconnectTimer = null;
      connect();
    }, delay);
  };
  const opened = (current: RealtimeSocket) => {
    if (socket !== current) return;
    socketOpen = true;
    failures = 0;
    sentSubscription = "";
    sendSubscription();
    sendPresence();
  };
  const disconnected = (current: RealtimeSocket, policy = false) => {
    if (socket !== current) return;
    socket = null;
    socketOpen = false;
    epoch = null;
    sequence = 0;
    failures += 1;
    if (policy) {
      client.clear();
      useChatStreamStore.getState().reset();
      chatCursor = null;
    }
    startFallback();
    scheduleReconnect();
  };
  const received = (current: RealtimeSocket, data: unknown) => {
    if (socket !== current) return;
    if (typeof data !== "string" || data.length > MAX_SERVER_FRAME) {
      if (runtime !== null) disconnected(current);
      current.close();
      return;
    }
    const frame = parseServerFrame(data);
    if (!frame) {
      if (runtime !== null) disconnected(current);
      current.close();
    } else {
      accept(frame, current);
    }
  };
  const connect = () => {
    if (closed || socket || identity === undefined) return;
    useChatStreamStore.getState().setStatus(failures === 0 ? "connecting" : "polling");
    if (runtime !== null) {
      const current = runtime.connectRealtime({
        open: () => opened(current),
        message: (data) => received(current, data),
        close: () => disconnected(current),
        error: () => disconnected(current),
      });
      socket = current;
      return;
    }
    const scheme = location.protocol === "https:" ? "wss:" : "ws:";
    const current = new WebSocket(`${scheme}//${location.host}/api/realtime`);
    socket = current;
    current.addEventListener("open", () => opened(current));
    current.addEventListener("message", (event) => received(current, event.data));
    current.addEventListener("error", () => current.close());
    current.addEventListener("close", (event) => disconnected(current, event.code === 1008));
  };
  const restartSocket = () => {
    if (closed) return;
    const current = socket;
    socketOpen = false;
    socket = null;
    epoch = null;
    sequence = 0;
    sentSubscription = "";
    if (reconnectTimer !== null) window.clearTimeout(reconnectTimer);
    reconnectTimer = null;
    startFallback();
    current?.close();
    queueMicrotask(connect);
  };

  startFallback();
  vkLoginTimer = window.setInterval(markVkLogin, VK_LOGIN_POLL_MS);
  if (runtime === null) presenceTimer = window.setInterval(sendPresence, PRESENCE_MS);
  queueMicrotask(() => {
    if (identity !== undefined) connect();
  });
  const unsubscribeQueries = client.getQueryCache().subscribe((event) => {
    sendSubscription();
    if (
      event.type === "updated" &&
      (event.action.type === "success" || event.action.type === "error") &&
      event.query.queryKey.length === 1 &&
      rootOf(event.query) === "me"
    ) {
      const next = accountIdentity(client);
      if (identity === undefined) {
        identity = next;
        connect();
      } else if (event.action.type === "success" && next !== identity) {
        identity = next;
        restartSocket();
      }
    }
  });
  const unsubscribePlayer = usePlayer.subscribe((state, previous) => {
    if (runtime === null && state.playing !== previous.playing) sendPresence();
    if (state.source !== previous.source) sendSubscription();
  });
  const reconnect = () => {
    if (!closed && !socket) {
      if (reconnectTimer !== null) window.clearTimeout(reconnectTimer);
      reconnectTimer = null;
      connect();
    }
  };
  window.addEventListener("online", reconnect);
  document.addEventListener("visibilitychange", reconnect);

  return () => {
    closed = true;
    unsubscribeQueries();
    unsubscribePlayer();
    window.removeEventListener("online", reconnect);
    document.removeEventListener("visibilitychange", reconnect);
    if (reconnectTimer !== null) window.clearTimeout(reconnectTimer);
    if (vkLoginTimer !== null) window.clearInterval(vkLoginTimer);
    if (presenceTimer !== null) window.clearInterval(presenceTimer);
    stopFallback();
    catchup.abort();
    scheduler.dispose();
    socketOpen = false;
    socket?.close();
    socket = null;
  };
}

export function useRealtime(): void {
  const client = useQueryClient();
  const chatEnabled = usePublicSettings().data?.chat_enabled === true;
  useEffect(() => startRealtime(client, chatEnabled), [client, chatEnabled]);
}
